// Thin wrappers over `/exporter/transit-orders/*`. One function per
// endpoint, each returns the unwrapped `BaseResponse.result`. The state
// machine endpoints (advance/jump) can return `422` with a populated
// `blockers[]` — we surface those by re-throwing the axios error so the
// hook layer can pull them off `error.response.data.result`.

import { api } from "@/lib/api/client"
import type { BaseResponse } from "@/types/api"

import type {
  AckDraftBLPayload,
  AttachLotsPayload,
  CreateContainerPayload,
  CreateOTPayload,
  DownloadUrlResponse,
  IssueCCCPayload,
  JumpStagePayload,
  ListOTsParams,
  OT,
  OTListItem,
  OTTraceability,
  OTTraceabilityGeo,
  MarkDefinitiveBLPayload,
  PatchContainerPayload,
  PatchOTPayload,
  RegisterCCCDocPayload,
  RegisterDraftBLPayload,
  RegisterEmpotageFilePayload,
  RegisterPhotoPayload,
  RegisterRedevancePayload,
  RegisterSlotDocPayload,
  CreateShareResponse,
  OTShareSummary,
  PublicSharePreview,
  PublicShareData,
  ShareTraceabilityPayload,
  UploadUrlPayload,
  UploadUrlResponse,
} from "./types"

const BASE = "/exporter/transit-orders"

// Only throw on `success: false`. Some endpoints (notably DELETE
// flows) return `{ success: true }` with no `result` body — treating
// that as a failure was incorrect and turned the FE's success toast
// into an error toast carrying the backend's status message.
//
// Mutation hooks now refetch the OT detail after success rather than
// relying on the response body, so it's safe to return `undefined`
// here when the backend doesn't include one. The TS return type is a
// soft promise the caller has to handle.
function unwrap<T>(data: BaseResponse<T>, fallback: string): T {
  if (!data.success) {
    throw new Error(data.message ?? fallback)
  }
  return data.result as T
}

// ── 5.1 OT CRUD ─────────────────────────────────────────────────

export async function listOTs(
  params: ListOTsParams = {},
): Promise<OTListItem[]> {
  const { data } = await api.get<BaseResponse<OTListItem[]>>(BASE, { params })
  return unwrap(data, "Could not load transit orders") ?? []
}

export async function getOT(otId: number | string): Promise<OT> {
  const { data } = await api.get<BaseResponse<OT>>(`${BASE}/${otId}`)
  return unwrap(data, "Could not load transit order")
}

export async function createOT(payload: CreateOTPayload): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(BASE, payload)
  return unwrap(data, "Could not create transit order")
}

export async function patchOT(
  otId: number | string,
  payload: PatchOTPayload,
): Promise<OT> {
  const { data } = await api.patch<BaseResponse<OT>>(`${BASE}/${otId}`, payload)
  return unwrap(data, "Could not update transit order")
}

export async function archiveOT(otId: number | string): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(`${BASE}/${otId}/archive`)
  return unwrap(data, "Could not archive transit order")
}

export async function unarchiveOT(otId: number | string): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(`${BASE}/${otId}/unarchive`)
  return unwrap(data, "Could not unarchive transit order")
}

// ── 5.2 Stage transitions ───────────────────────────────────────

export async function advanceOT(otId: number | string): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(`${BASE}/${otId}/advance`)
  return unwrap(data, "Could not advance stage")
}

export async function jumpOT(
  otId: number | string,
  payload: JumpStagePayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/jump`,
    payload,
  )
  return unwrap(data, "Could not jump stage")
}

// ── 5.3 Lots ────────────────────────────────────────────────────

export async function attachLots(
  otId: number | string,
  payload: AttachLotsPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/lots`,
    payload,
  )
  return unwrap(data, "Could not attach lots")
}

export async function detachLot(
  otId: number | string,
  lotId: number | string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}`,
  )
  return unwrap(data, "Could not detach lot")
}

// ── 5.4 CCC ─────────────────────────────────────────────────────

export async function issueCCC(
  otId: number | string,
  lotId: number | string,
  payload: IssueCCCPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}/ccc`,
    payload,
  )
  return unwrap(data, "Could not record CCC decision")
}

export async function clearCCC(
  otId: number | string,
  lotId: number | string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}/ccc`,
  )
  return unwrap(data, "Could not clear CCC decision")
}

export async function registerCCCDoc(
  otId: number | string,
  lotId: number | string,
  payload: RegisterCCCDocPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}/ccc/docs`,
    payload,
  )
  return unwrap(data, "Could not register CCC doc")
}

export async function deleteCCCDoc(
  otId: number | string,
  lotId: number | string,
  docId: string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}/ccc/docs/${docId}`,
  )
  return unwrap(data, "Could not delete CCC doc")
}

export async function reprocessLot(
  otId: number | string,
  lotId: number | string,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/lots/${lotId}/reprocess`,
  )
  return unwrap(data, "Could not flag lot for reprocessing")
}

// ── 5.5 Containers ──────────────────────────────────────────────

export async function createContainer(
  otId: number | string,
  payload: CreateContainerPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/containers`,
    payload,
  )
  return unwrap(data, "Could not create container")
}

export async function patchContainer(
  otId: number | string,
  containerId: number,
  payload: PatchContainerPayload,
): Promise<OT> {
  const { data } = await api.patch<BaseResponse<OT>>(
    `${BASE}/${otId}/containers/${containerId}`,
    payload,
  )
  return unwrap(data, "Could not update container")
}

export async function deleteContainer(
  otId: number | string,
  containerId: number,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/containers/${containerId}`,
  )
  return unwrap(data, "Could not delete container")
}

// ── 5.6 Empotage sub-lists ──────────────────────────────────────

export async function registerPhoto(
  otId: number | string,
  payload: RegisterPhotoPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/photos`,
    payload,
  )
  return unwrap(data, "Could not register photo")
}

export async function deletePhoto(
  otId: number | string,
  photoId: string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/photos/${photoId}`,
  )
  return unwrap(data, "Could not delete photo")
}

export async function registerEmpotageFile(
  otId: number | string,
  payload: RegisterEmpotageFilePayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/empotage-files`,
    payload,
  )
  return unwrap(data, "Could not register file")
}

export async function deleteEmpotageFile(
  otId: number | string,
  fileId: string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/empotage-files/${fileId}`,
  )
  return unwrap(data, "Could not delete file")
}

// ── 5.7 Formule ─────────────────────────────────────────────────

export async function registerRedevance(
  otId: number | string,
  payload: RegisterRedevancePayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/redevances`,
    payload,
  )
  return unwrap(data, "Could not register redevance")
}

export async function deleteRedevance(
  otId: number | string,
  redevanceId: string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/redevances/${redevanceId}`,
  )
  return unwrap(data, "Could not delete redevance")
}

// ── 5.8 Embarquement ────────────────────────────────────────────

export async function registerDraftBL(
  otId: number | string,
  payload: RegisterDraftBLPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/draft-bl`,
    payload,
  )
  return unwrap(data, "Could not register draft BL")
}

export async function ackDraftBL(
  otId: number | string,
  payload: AckDraftBLPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/draft-bl/ack`,
    payload,
  )
  return unwrap(data, "Could not acknowledge draft BL weight")
}

export async function markDefinitiveBL(
  otId: number | string,
  draftId: number | string,
  payload: MarkDefinitiveBLPayload,
): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/draft-bl/${draftId}/mark`,
    payload,
  )
  return unwrap(data, "Could not mark draft BL as definitive")
}

export async function unmarkDefinitiveBL(otId: number | string): Promise<OT> {
  const { data } = await api.post<BaseResponse<OT>>(
    `${BASE}/${otId}/draft-bl/unmark`,
  )
  return unwrap(data, "Could not unmark definitive BL")
}

// ── 5.9 Slot documents ──────────────────────────────────────────

export async function upsertSlotDoc(
  otId: number | string,
  slotId: string,
  payload: RegisterSlotDocPayload,
): Promise<OT> {
  const { data } = await api.put<BaseResponse<OT>>(
    `${BASE}/${otId}/docs/${slotId}`,
    payload,
  )
  return unwrap(data, "Could not register document")
}

export async function deleteSlotDoc(
  otId: number | string,
  slotId: string,
): Promise<OT> {
  const { data } = await api.delete<BaseResponse<OT>>(
    `${BASE}/${otId}/docs/${slotId}`,
  )
  return unwrap(data, "Could not delete document")
}

// ── 5.10 Upload helpers ─────────────────────────────────────────

export async function createUploadUrl(
  otId: number | string,
  payload: UploadUrlPayload,
): Promise<UploadUrlResponse> {
  const { data } = await api.post<BaseResponse<UploadUrlResponse>>(
    `${BASE}/${otId}/upload-url`,
    payload,
  )
  return unwrap(data, "Could not get upload URL")
}

export async function getDownloadUrl(
  otId: number | string,
  storedKey: string,
): Promise<DownloadUrlResponse> {
  const { data } = await api.get<BaseResponse<DownloadUrlResponse>>(
    `${BASE}/${otId}/download-url`,
    { params: { storedKey } },
  )
  return unwrap(data, "Could not get download URL")
}

export async function putToPresignedUrl(
  url: string,
  file: File,
): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: file.type ? { "Content-Type": file.type } : undefined,
  })
  if (!res.ok) {
    throw new Error(`Upload failed: ${res.status} ${res.statusText}`)
  }
}

// ── 5.11 Terminer ───────────────────────────────────────────────

export async function getTraceability(
  otId: number | string,
): Promise<OTTraceability> {
  const { data } = await api.get<BaseResponse<OTTraceability>>(
    `${BASE}/${otId}/traceability`,
  )
  const result = unwrap(data, "Could not load traceability")
  // Defensive boundary normalisation: pq.StringArray can serialise to
  // `null` for empty postgres arrays even though our TS type says
  // `string[]`. Coerce so consumers can rely on `.join(...)` etc.
  return {
    ...result,
    producers: (result.producers ?? []).map((p) => ({
      ...p,
      certificates: p.certificates ?? [],
    })),
    parcels: (result.parcels ?? []).map((p) => ({
      ...p,
      certifications: p.certifications ?? [],
    })),
    transactions: (result.transactions ?? []).map((tx) => ({
      ...tx,
      certificates: tx.certificates ?? [],
    })),
  }
}

export async function getTraceabilityGeo(
  otId: number | string,
): Promise<OTTraceabilityGeo> {
  const { data } = await api.get<BaseResponse<OTTraceabilityGeo>>(
    `${BASE}/${otId}/traceability/geo`,
  )
  const result = unwrap(data, "Could not load parcel geometry")
  return { parcels: result.parcels ?? [] }
}

// Mints a public, PIN-gated share link and emails the recipient. The
// PIN itself is never returned to the operator — it's only delivered
// via email so it can't accidentally leak through screenshots / chat.
export async function createTraceabilityShare(
  otId: number | string,
  payload: ShareTraceabilityPayload,
): Promise<CreateShareResponse> {
  const { data } = await api.post<BaseResponse<CreateShareResponse>>(
    `${BASE}/${otId}/share-traceability`,
    payload,
  )
  return unwrap(data, "Could not create share")
}

export async function listTraceabilityShares(
  otId: number | string,
): Promise<OTShareSummary[]> {
  const { data } = await api.get<BaseResponse<OTShareSummary[]>>(
    `${BASE}/${otId}/share-traceability`,
  )
  return unwrap(data, "Could not load shares") ?? []
}

export async function revokeTraceabilityShare(
  otId: number | string,
  shareId: number | string,
): Promise<OTShareSummary> {
  const { data } = await api.post<BaseResponse<OTShareSummary>>(
    `${BASE}/${otId}/share-traceability/${shareId}/revoke`,
  )
  return unwrap(data, "Could not revoke share")
}

export async function resendTraceabilityShare(
  otId: number | string,
  shareId: number | string,
): Promise<OTShareSummary> {
  const { data } = await api.post<BaseResponse<OTShareSummary>>(
    `${BASE}/${otId}/share-traceability/${shareId}/resend`,
  )
  return unwrap(data, "Could not resend share")
}

// ── Public share viewer (no auth) ───────────────────────────────

export async function getPublicSharePreview(
  code: string,
): Promise<PublicSharePreview> {
  const { data } = await api.get<BaseResponse<PublicSharePreview>>(
    `/public/transit-shares/${code}`,
  )
  return unwrap(data, "Share not found")
}

export async function accessPublicShare(
  code: string,
  pin: string,
): Promise<PublicShareData> {
  const { data } = await api.post<BaseResponse<PublicShareData>>(
    `/public/transit-shares/${code}/access`,
    { pin },
  )
  const result = unwrap(data, "Could not load share")
  // Same null-array coercion as getTraceability — the public response
  // wraps the same wire shape so we apply the guard here too.
  return {
    ...result,
    traceability: {
      ...result.traceability,
      producers: (result.traceability.producers ?? []).map((p) => ({
        ...p,
        certificates: p.certificates ?? [],
      })),
      parcels: (result.traceability.parcels ?? []).map((p) => ({
        ...p,
        certifications: p.certifications ?? [],
      })),
      transactions: (result.traceability.transactions ?? []).map((tx) => ({
        ...tx,
        certificates: tx.certificates ?? [],
      })),
    },
    parcelGeo: result.parcelGeo ?? [],
  }
}
